import { __decorate, __param } from "tslib";
import { UUID } from '@genesislcap/foundation-utils';
import { DI } from '@microsoft/fast-foundation';
import { AuthType } from '../auth/auth.types';
import { Session } from '../session';
/**
 * General Message types
 * @public
 */
export var MessageType;
(function (MessageType) {
    MessageType["LOGON_ACK"] = "LOGON_ACK";
    MessageType["LOGON_NACK"] = "LOGON_NACK";
    MessageType["LOGOFF_ACK"] = "LOGOFF_ACK";
    MessageType["LOGOFF_NACK"] = "LOGOFF_NACK";
    MessageType["HEARTBEAT_PING"] = "HEARTBEAT_PING";
    MessageType["HEARTBEAT_PONG"] = "HEARTBEAT_PONG";
    MessageType["MORE_ROWS"] = "MORE_ROWS";
    MessageType["MSG_ACK"] = "MSG_ACK";
    MessageType["MSG_NACK"] = "MSG_NACK";
    MessageType["LOGOUT_ACK"] = "LOGOUT_ACK";
    MessageType["LOGOUT_NACK"] = "LOGOUT_NACK";
    MessageType["QUERY_UPDATE"] = "QUERY_UPDATE";
})(MessageType || (MessageType = {}));
/**
 * Event Message types
 * @public
 */
export var EventMessageType;
(function (EventMessageType) {
    EventMessageType["EVENT_LOGIN_AUTH"] = "EVENT_LOGIN_AUTH";
    EventMessageType["EVENT_LOGIN_AUTH_ACK"] = "EVENT_LOGIN_AUTH_ACK";
    EventMessageType["EVENT_LOGIN_AUTH_NACK"] = "EVENT_LOGIN_AUTH_NACK";
    EventMessageType["EVENT_LOGOUT"] = "EVENT_LOGOUT";
    EventMessageType["DATA_LOGON"] = "DATA_LOGON";
    EventMessageType["DATA_LOGOFF"] = "DATA_LOGOFF";
    EventMessageType["META_REQUEST"] = "META_REQUEST";
    EventMessageType["JSON_SCHEMA_REQUEST"] = "JSON_SCHEMA_REQUEST";
    EventMessageType["RESOURCES_REQUEST"] = "RESOURCES_REQUEST";
    EventMessageType["EVENT_CHANGE_USER_PASSWORD"] = "EVENT_CHANGE_USER_PASSWORD";
    EventMessageType["EVENT_SELF_SERVICE_PASSWORD_RESET"] = "EVENT_SELF_SERVICE_PASSWORD_RESET";
    EventMessageType["EVENT_PASSWORD_RESET_ACTION"] = "EVENT_PASSWORD_RESET_ACTION";
})(EventMessageType || (EventMessageType = {}));
/**
 * General Message codes
 * @public
 */
export var MessageCode;
(function (MessageCode) {
    MessageCode["SESSION_NOT_FOUND"] = "SESSION_NOT_FOUND";
    MessageCode["UNKNOWN_MESSAGE_TYPE"] = "UNKNOWN_MESSAGE_TYPE";
})(MessageCode || (MessageCode = {}));
/**
 * Check if the provided message contains an error and throw an Error if it does
 * @public
 * @param message - Message to check for errors
 * @returns The message if no errors are present, otherwise throws an Error
 */
export const messageOrThrow = (message) => {
    if (message.ERROR) {
        throw new Error(message.ERROR.map((error) => error.TEXT).join());
    }
    return message;
};
/**
 * TODO: Abstract out localStorage interaction with shell io stuff
 */
let MessageBuilderService = class MessageBuilderService {
    /**
     * UUID will be created in the DI with the App specific requirements.
     * Keeping it encapsulated means messages will always conform.
     */
    constructor(session, uuid) {
        this.session = session;
        this.uuid = uuid;
    }
    createLoginMessage(credentials) {
        let DETAILS;
        switch (credentials.type) {
            case AuthType.BASIC:
                DETAILS = {
                    USER_NAME: credentials.username,
                    PASSWORD: credentials.password,
                };
                break;
            case AuthType.REFRESH:
                DETAILS = {
                    USER_NAME: credentials.username,
                    REFRESH_AUTH_TOKEN: credentials.refreshToken,
                    MFA_CODE: credentials.mfaToken,
                };
                break;
            case AuthType.SSO:
                DETAILS = {
                    SSO_TOKEN: credentials.ssoToken,
                };
                break;
            case AuthType.JWT:
                DETAILS = {
                    JWT: credentials.jwt,
                    DOMAIN: credentials.domain,
                };
                break;
        }
        return {
            DETAILS,
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: EventMessageType.EVENT_LOGIN_AUTH,
        };
    }
    createLogoutMessage(loginResult) {
        var _a, _b;
        return {
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: EventMessageType.EVENT_LOGOUT,
            SESSION_AUTH_TOKEN: (_a = loginResult.authToken) !== null && _a !== void 0 ? _a : this.session.getSessionStorageItem('authToken'),
            SESSION_ID: (_b = loginResult.sessionId) !== null && _b !== void 0 ? _b : this.session.getSessionStorageItem('sessionId'),
        };
    }
    createDataLogonMessage(resourceName, params) {
        return {
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: EventMessageType.DATA_LOGON,
            DETAILS: Object.assign({ DATASOURCE_NAME: resourceName }, params),
            SESSION_AUTH_TOKEN: this.session.getSessionStorageItem('authToken'),
        };
    }
    createDataLogoffMessage(sourceRef) {
        return {
            SOURCE_REF: sourceRef,
            MESSAGE_TYPE: EventMessageType.DATA_LOGOFF,
            SESSION_AUTH_TOKEN: this.session.getSessionStorageItem('authToken'),
        };
    }
    createRequestMessage(resourceName, params) {
        return Object.assign({ SOURCE_REF: this.uuid.createId(), SESSION_AUTH_TOKEN: this.session.getSessionStorageItem('authToken'), MESSAGE_TYPE: resourceName.startsWith('REQ_') ? resourceName : `REQ_${resourceName}` }, params);
    }
    createMetaRequestMessage(resourceName, messageType = EventMessageType.META_REQUEST) {
        return {
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: messageType,
            DETAILS: {
                FEATURE: resourceName,
            },
            SESSION_AUTH_TOKEN: this.session.getSessionStorageItem('authToken'),
        };
    }
    createCommitMessage(eventName, params) {
        return Object.assign({ SOURCE_REF: this.uuid.createId(), SESSION_AUTH_TOKEN: this.session.getSessionStorageItem('authToken'), MESSAGE_TYPE: eventName }, params);
    }
    createRefreshTokenMessage() {
        return {
            SOURCE_REF: this.uuid.createId(),
            SESSION_AUTH_TOKEN: this.session.getSessionStorageItem('authToken'),
            MESSAGE_TYPE: EventMessageType.EVENT_LOGIN_AUTH,
            DETAILS: {
                REFRESH_AUTH_TOKEN: this.session.getSessionStorageItem('refreshToken'),
            },
        };
    }
    createChangePasswordMessage(username, oldPassword, newPassword) {
        return {
            USER_NAME: username,
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: EventMessageType.EVENT_CHANGE_USER_PASSWORD,
            DETAILS: {
                OLD_PASSWORD: oldPassword,
                NEW_PASSWORD: newPassword,
            },
        };
    }
    createForgotPasswordMessage(username, returnUrl, requester = username) {
        return {
            USER_NAME: requester,
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: EventMessageType.EVENT_SELF_SERVICE_PASSWORD_RESET,
            DETAILS: {
                USER_NAME: username,
                RETURN_URL: returnUrl,
            },
        };
    }
    createForgotPasswordTokenMessage(username, resetToken, newPassword, requester = username) {
        return {
            USER_NAME: requester,
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: EventMessageType.EVENT_PASSWORD_RESET_ACTION,
            DETAILS: {
                USER_NAME: username,
                RESET_TOKEN: resetToken,
                NEW_PASSWORD: newPassword,
            },
        };
    }
    createMoreRowsMessage(sourceRef) {
        return {
            SOURCE_REF: this.uuid.createId(),
            MESSAGE_TYPE: MessageType.MORE_ROWS,
            DETAILS: {
                SOURCE_REF: sourceRef,
            },
            SESSION_AUTH_TOKEN: this.session.getSessionStorageItem('authToken'),
        };
    }
    createHTTPHeadersFromMessage(message, contentType = 'application/json; charset=UTF-8') {
        const headers = { 'Content-type': contentType };
        if (message.SOURCE_REF) {
            headers['source-ref'] = message.SOURCE_REF;
            headers['SOURCE_REF'] = message.SOURCE_REF;
        }
        if (message.SESSION_AUTH_TOKEN) {
            headers['SESSION-AUTH-TOKEN'] = message.SESSION_AUTH_TOKEN;
        }
        if (message.MESSAGE_TYPE === EventMessageType.EVENT_LOGOUT) {
            headers['SESSION-ID'] = message.SESSION_ID;
        }
        return headers;
    }
};
MessageBuilderService = __decorate([
    __param(0, Session),
    __param(1, UUID)
], MessageBuilderService);
/**
 * The DI token for the MessageBUilder interface.
 * @internal
 */
export const MessageBuilder = DI.createInterface((x) => x.singleton(MessageBuilderService));
